import { useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL } from 'shared/constants/api-url';
import { useGetCompanyGroups } from 'features/get-company-groups';
import { useGetCompanyRoles } from 'features/get-company-roles';
import { TextField as MuiTextField, MenuItem, FormControl, InputLabel, Select } from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  styled
} from '@mui/material';
import { useRecordContext, useRefresh } from 'react-admin';
import { getAdminData } from 'app/providers/auth-provider';
import { SessionBalanceList } from './session-balance-list';
import { AddSessionBalanceForm } from './add-session-balance-form';

const UsedChoices = [
  { id: 'used', name: 'Использованные' },
  { id: 'unused', name: 'Доступные' },
  { id: 'not_started', name: 'Не доступные' },
  { id: 'expired', name: 'Истекшие' },
];

const SearchTermChoices = [
  { id: 'consumerEmail', name: 'Email пользователя' },
  { id: 'adminName', name: 'Имя создателя баланса консультаций' },
];

export const SessionBalances = () => {
  const { id } = useParams();
  const record = useRecordContext();
  const refresh = useRefresh();

  const [modalOpen, setModalOpen] = useState(false);
  const { userRole } = getAdminData();

  const isSuperadmin = userRole === 'superadmin';

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  const handleAddSuccess = useCallback(() => {
    handleCloseModal();
    refresh();
  }, [refresh]);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [sessionBalanceStatus, setSessionBalanceStatus] = useState('');
  const [page, setPage] = useState(1);
  const [companyGroup, setCompanyGroup] = useState<string>('');
  const [companyRole, setCompanyRole] = useState<string>('');
  const [perPage, setPerPage] = useState(10);

  const { groups = [], isLoading: isLoadingGroups } = useGetCompanyGroups({ companyId: id || '' });
  const { roles = [], isLoading: isLoadingRoles } = useGetCompanyRoles({ companyId: id || '' });

  const url = useMemo(() => {
    const params = new URLSearchParams({
      companyId: id || '',
      limit: perPage.toString(),
      page: page.toString(),
    });

    if (searchTerm) params.append('searchTerm', searchTerm);
    if (searchValue && searchTerm) params.append('searchValue', searchValue);
    if (sessionBalanceStatus) params.append(sessionBalanceStatus, 'true');
    if (companyGroup) params.append('group', companyGroup);
    if (companyRole) params.append('role', companyRole);

    return `${API_URL.getSessionBalances}?${params.toString()}`;
  }, [
    id,
    perPage,
    page,
    searchTerm,
    searchValue,
    sessionBalanceStatus,
    companyGroup,
    companyRole,
  ]);

  const filters = useMemo(() => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
      <div style={{ display: 'flex', gap: 16 }}>
        <MuiTextField
          value={searchValue || ''}
          onChange={(e) => setSearchValue(e.target.value)}
          label="Строка для поиска"
          placeholder="Строка для поиска"
          variant="standard"
          style={{ minWidth: 200 }}
        />
        <FormControl variant="standard" style={{ minWidth: 200 }}>
          <InputLabel>Искать по:</InputLabel>
          <Select
            value={searchTerm || ''}
            onChange={(e) => setSearchTerm(e.target.value)}
            label="Искать по:"
          >
            {SearchTermChoices.map(choice => (
              <MenuItem key={choice.id} value={choice.id}>{choice.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        <FormControl variant="standard" style={{ minWidth: 200 }}>
          <InputLabel>Статус</InputLabel>
          <Select
            value={sessionBalanceStatus || ''}
            onChange={(e) => setSessionBalanceStatus(e.target.value)}
            label="Статус"
          >
            <MenuItem value="">Все</MenuItem>
            {UsedChoices.map(choice => (
              <MenuItem key={choice.id} value={choice.id}>{choice.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="standard" style={{ minWidth: 200 }} disabled={isLoadingGroups}>
          <InputLabel>Группа</InputLabel>
          <Select
            value={companyGroup}
            onChange={(e) => setCompanyGroup(e.target.value)}
            label="Группа"
          >
            <MenuItem value="">Все</MenuItem>
            {groups.map(group => (
              <MenuItem key={group} value={group}>{group}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="standard" style={{ minWidth: 200 }} disabled={isLoadingRoles}>
          <InputLabel>Роль</InputLabel>
          <Select
            value={companyRole}
            onChange={(e) => setCompanyRole(e.target.value)}
            label="Роль"
          >
            <MenuItem value="">Все</MenuItem>
            {roles.map(role => (
              <MenuItem key={role} value={role}>{role}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    </div>
  ), [searchValue, searchTerm, sessionBalanceStatus, companyGroup, companyRole, groups, roles, isLoadingGroups, isLoadingRoles]);

  return (
    <>
      {record.type === 'balance' && (
        <AddButton
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenModal}
          disabled={!isSuperadmin}
        >
          Добавить балансы консультаций
        </AddButton>
      )}

      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <StyledDialogTitle>
          Добавление балансов консультаций
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>
        <DialogContent>
          <AddSessionBalanceForm onSuccess={handleAddSuccess} />
        </DialogContent>
      </Dialog>
      <div style={{ marginBottom: 16 }}>
        {filters}
      </div>
      <SessionBalanceList 
        url={url}
        page={page}
        perPage={perPage}
        setPage={setPage}
        setPerPage={setPerPage}
      />
    </>
  );
};

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const AddButton = styled(Button)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[2]
  }
}));